/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { FC, useState } from 'react';
import useNotifications from '../../../../hooks/use-notifications';
import { QSTypography } from '../../../atoms';
import { Box, ButtonBase, Link } from '@mui/material';
import QSTooltip from '../../../molecules/tooltip';
import { ReactComponent as CopyIcon } from '../../../../../shared/assets/svg/copy.svg';

interface Message {
  title: string;
  titleKey: string;
  text: string;
  textKey: string;
  textToCopy: string;
}

export interface DataItem {
  id: number;
  title: string;
  titleKey: string;
  message?: Message;
}

const StepRenderer: FC<{ item: DataItem; website?: string }> = ({
  item,
  website,
}) => {
  const { addNotification } = useNotifications();
  const { id, title, message } = item;
  const [copyBtnText, setCopyBtnText] = useState('Copy');
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        (message?.textToCopy ?? '') + (website ?? '[Your website]')
      );
      setCopyBtnText('Copied');
      setShowCopiedTooltip(true);
      setTimeout(() => {
        setCopyBtnText('Copy');
        setShowCopiedTooltip(false);
      }, 3000);
    } catch (e) {
      addNotification({
        message: 'Failed to copy to clipboard',
        type: 'error',
      });
    }
  };
  return (
    <Box
      css={css`
        display: flex;
        align-items: flex-start;
        gap: 8px;
        align-self: stretch;
      `}
    >
      <Box
        css={css`
          display: flex;
          width: 24px;
          height: 24px;
          padding: 8px;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 8px;
          border-radius: 50%;
          background: rgba(63, 144, 109, 0.1);
        `}
      >
        <QSTypography
          css={css`
            color: #3f906d;
            font-size: 14px;
            font-weight: 600;
            line-height: 160%;
          `}
        >
          {id}
        </QSTypography>
      </Box>
      <Box
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 8px;
          flex: 1 0 0;
        `}
      >
        <QSTypography
          css={css`
            color: #2d2d2d;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
            align-self: flex-start;
          `}
        >
          {title}
        </QSTypography>
        {message ? (
          <Box
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              align-self: stretch;
              border-radius: 4px;
              border: 1px solid #e0e0e9;
              background: #fbfbfc;
              text-align: left;
            `}
          >
            <Box
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                align-self: stretch;
                border-bottom: 1px solid #e0e0e9;
                padding: 10px 0px;
                margin: 0px 16px;
              `}
            >
              <QSTypography
                css={css`
                  flex: 1 0 0;
                  color: #2d2d2d;
                  font-family: Inter;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 160%;
                `}
              >
                {message.title}
              </QSTypography>
              <ButtonBase
                css={css`
                  display: flex;
                  padding: 4px 5px;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  border-radius: 4px;
                `}
                onClick={handleCopy}
              >
                <Box
                  css={css`
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <CopyIcon
                    css={css`
                      width: 18px;
                      height: 18px;
                      flex-shrink: 0;
                      * {
                        stroke: #3f906d;
                      }
                    `}
                  />
                  <QSTypography
                    css={css`
                      color: #3f906d;
                      font-size: 13px;
                      font-weight: 500;
                      line-height: 22px;
                      letter-spacing: -0.13px;
                    `}
                  >
                    {copyBtnText}
                  </QSTypography>
                </Box>
              </ButtonBase>
            </Box>
            <QSTooltip
              title={'Copied'}
              placement="top"
              arrow={false}
              open={showCopiedTooltip}
            >
              <ButtonBase
                css={css`
                  text-align: left;
                  padding: 16px;
                  padding-top: 3px;
                `}
                onClick={handleCopy}
              >
                <QSTypography
                  css={css`
                    color: #2d2d2d;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 160%;
                    padding-top: 3px;

                    span {
                      color: #3796ff;
                      font-size: 14px;
                      font-weight: 400;
                      line-height: 160%;
                    }
                  `}
                >
                  {message.textToCopy}{' '}
                  <Link
                    css={css`
                      color: #3796ff;
                      font-family: Inter;
                      font-size: 14px;
                      font-weight: 400;
                      line-height: 160%;
                    `}
                    href={website}
                    target="_blank"
                  >
                    {website ?? '[Your website]'}
                  </Link>
                </QSTypography>
              </ButtonBase>
            </QSTooltip>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default StepRenderer;
